/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import MDDropzone from "components/MDDropzone";
import TextField from "@mui/material/TextField";
import MDDatePicker from "components/MDDatePicker";
import swal from "sweetalert";
import Autocomplete from "@mui/material/Autocomplete";
import MDInput from "components/MDInput";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Link from "@mui/material/Link";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/Info";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PendingIcon from "@mui/icons-material/Pending";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteIcon from "@mui/icons-material/Delete";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD, API_CHAT } from "utils";
import { logoutUser } from "actions/AuthAction";
import { forceLogoutInactive } from "utils/forceLogoutInactive";
import { useNavigate } from "react-router-dom";
import ChatIcon from "@mui/icons-material/Chat";
import RefreshIcon from "@mui/icons-material/Refresh";

// Status colors
const STATUS_COLORS = {
  Belum: "warning",
  Proses: "info",
  Selesai: "success",
};

function DisposisiTindakLanjut() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [disposisiList, setDisposisiList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPenerima, setSelectedPenerima] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [userId, setUserId] = useState(null);
  const [dinasId, setDinasId] = useState(null);
  const [levelJabatan, setLevelJabatan] = useState(null);
  const [activeFilter, setActiveFilter] = useState("belum_selesai"); // default: belum_selesai
  const [tahunFilter, setTahunFilter] = useState(new Date().getFullYear()); // untuk filter Total, default: current year
  const [statistik, setStatistik] = useState({
    total_disposisi: 0,
    total_proses: 0,
    total_selesai: 0,
    total_belum_dibaca: 0,
    total_belum_selesai: 0,
  });
  const [openTindakLanjutDialog, setOpenTindakLanjutDialog] = useState(false);
  const [selectedDisposisi, setSelectedDisposisi] = useState(null);
  const [files, setFiles] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [showSizeWarning, setShowSizeWarning] = useState(false);
  const dropzoneRef = useRef(null);
  const [keteranganTL, setKeteranganTL] = useState("");
  const [tanggalTL, setTanggalTL] = useState(new Date());
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [openLanjutkanDialog, setOpenLanjutkanDialog] = useState(false);
  const [penerimaOptions, setPenerimaOptions] = useState([]);
  const [loadingPenerima, setLoadingPenerima] = useState(false);
  const [selectedPenerimaLanjut, setSelectedPenerimaLanjut] = useState([]);
  const [catatanDisposisi, setCatatanDisposisi] = useState("");
  const [tingkatPerintah, setTingkatPerintah] = useState("");
  const lanjutkanDropzoneRef = useRef(null);
  const [openPilihanDialog, setOpenPilihanDialog] = useState(false);
  const [openLanjutanDialog, setOpenLanjutanDialog] = useState(false);
  const [selectedLanjutan, setSelectedLanjutan] = useState(null);
  const [openTindakLanjutPdfDialog, setOpenTindakLanjutPdfDialog] =
    useState(false);

  const [statusUser, setStatusUser] = useState(null);
  const [selectedTindakLanjutPdf, setSelectedTindakLanjutPdf] = useState(null);
  const [openChatDialog, setOpenChatDialog] = useState(false);
  const [selectedChatDisposisi, setSelectedChatDisposisi] = useState(null);
  const [selectedChatPenerima, setSelectedChatPenerima] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loadingChat, setLoadingChat] = useState(false);
  const [refreshingChat, setRefreshingChat] = useState(false);
  const chatEndRef = useRef(null);
  const navigate = useNavigate();
  // Dropzone options untuk Tindak Lanjut (Selesai) - 3 MB
  const dropzoneOptionsTindakLanjut = useMemo(
    () => ({
      addRemoveLinks: true,
      uploadMultiple: true,
      maxFilesize: 3,
      maxFiles: 5,
      maxTotalFilesize: 3,
      acceptedFiles: ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg",
      dictDefaultMessage: "Klik atau seret file ke sini untuk mengunggah",
      dictFileTooBig:
        "File terlalu besar ({{filesize}}MB). Maksimal ukuran file: {{maxFilesize}}MB",
      dictTotalFilesTooBig: "Total ukuran file melebihi batas maksimal (3MB)",
      dictInvalidFileType:
        "Format file tidak didukung. Hanya PDF, DOC/DOCX, XLS/XLSX, dan JPG/JPEG yang diperbolehkan",
      dictResponseError: "Terjadi kesalahan saat mengunggah file",
      dictCancelUpload: "Batalkan unggah",
      dictUploadCanceled: "Unggah dibatalkan",
      dictCancelUploadConfirmation:
        "Apakah Anda yakin ingin membatalkan unggah?",
      dictRemoveFile: "Hapus file",
      dictMaxFilesExceeded: "Anda tidak dapat mengunggah file lagi",
    }),
    []
  );
  // Dropzone options untuk Lanjutkan - 1 MB
  const dropzoneOptionsLanjutkan = useMemo(
    () => ({
      addRemoveLinks: true,
      uploadMultiple: true,
      maxFilesize: 1,
      maxFiles: 5,
      maxTotalFilesize: 1,
      acceptedFiles: ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg",
      dictDefaultMessage: "Klik atau seret file ke sini untuk mengunggah",
      dictFileTooBig:
        "File terlalu besar ({{filesize}}MB). Maksimal ukuran file: {{maxFilesize}}MB",
      dictTotalFilesTooBig: "Total ukuran file melebihi batas maksimal (1MB)",
      dictInvalidFileType:
        "Format file tidak didukung. Hanya PDF, DOC/DOCX, XLS/XLSX, dan JPG/JPEG yang diperbolehkan",
      dictResponseError: "Terjadi kesalahan saat mengunggah file",
      dictCancelUpload: "Batalkan unggah",
      dictUploadCanceled: "Unggah dibatalkan",
      dictCancelUploadConfirmation:
        "Apakah Anda yakin ingin membatalkan unggah?",
      dictRemoveFile: "Hapus file",
      dictMaxFilesExceeded: "Anda tidak dapat mengunggah file lagi",
    }),
    []
  );

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setUserId(user.uid);
      setDinasId(user.dinas_id);
      setLevelJabatan(user.level_jabatan);
      setStatusUser(user.status);
    } else {
      logoutUser(navigate);
    }
  }, []);

  useEffect(() => {
    if (userId) {
      fetchStatistik();
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchDisposisi();
    }
  }, [userId, activeFilter, tahunFilter]);

  useEffect(() => {
    // Fetch penerima options when component mounts
    if (!dinasId || !userId || levelJabatan === null) {
      return;
    }
    const fetchPenerimaOptions = async () => {
      try {
        setLoadingPenerima(true);
        // Backend akan mengambil bidang_id dari database berdasarkan userId
        const url =
          statusUser === "pimpinan"
            ? `${API_ESA_OFFICE}users/pimpinan/list`
            : `${API_ESA_OFFICE}users/dinas/${dinasId}`;

        const response = await axios.get(url);
        const options = response.data.map((user) => ({
          value: user.user_id,
          label: user.nama,
          avatar: user.uid_RSS
            ? `https://smartdh.rotendaokab.go.id/uploads/${user.uid_RSS}.jpg`
            : null,
        }));
        setPenerimaOptions(options);
      } catch (error) {
        console.error("Error fetching penerima options:", error);
        swal({
          icon: "error",
          title: "Oops...",
          text: "Gagal mengambil data penerima",
          button: "OK",
        });
      } finally {
        setLoadingPenerima(false);
      }
    };
    fetchPenerimaOptions();
  }, [dinasId, userId, levelJabatan, statusUser]);

  const fetchDisposisi = async () => {
    try {
      setLoading(true);
      // filter: all | belum_selesai | proses | selesai | belum_dibaca
      // filter tahun hanya untuk Total (all) dan Selesai
      let url = `${API_ESA_OFFICE}disposisi/disposisi-penerima/${userId}?filter=${activeFilter}`;
      if (activeFilter === "all" || activeFilter === "selesai") {
        url += `&tahun=${tahunFilter}`;
      }
      const response = await axios.get(url);
      const processedData = response.data.map((item) => ({
        ...item,
        file_surat: item.file_surat
          ? `${API_ESA_OFFICE_UPLOAD}/uploads/${item.file_surat}`
          : null,
        profil_pengirim: item.profil_pengirim
          ? `${API_ESA_OFFICE_UPLOAD}${item.profil_pengirim}`
          : null,
        penerima: {
          ...item.penerima,
          profil: item.penerima.profil
            ? `${API_ESA_OFFICE_UPLOAD}${item.penerima.profil}`
            : null,
          file_disposisi: item.penerima.file_disposisi
            ? (() => {
                try {
                  const files = JSON.parse(item.penerima.file_disposisi);
                  return Array.isArray(files)
                    ? files.map(
                        (file) =>
                          `${API_ESA_OFFICE_UPLOAD}/uploads/disposisi/${file}`
                      )
                    : [];
                } catch (error) {
                  console.error("Error parsing file_disposisi:", error);
                  return [];
                }
              })()
            : [],
          file_TL_disposisi: item.penerima.file_TL_disposisi
            ? (() => {
                try {
                  const files = JSON.parse(item.penerima.file_TL_disposisi);
                  return Array.isArray(files)
                    ? files.map((file) => `${API_ESA_OFFICE_UPLOAD}${file}`)
                    : [];
                } catch (error) {
                  console.error("Error parsing file_TL_disposisi:", error);
                  return [];
                }
              })()
            : [],
        },
      }));

      setDisposisiList(processedData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching disposisi:", error);
      setLoading(false);
    }
  };

  const fetchStatistik = async () => {
    try {
      // Statistik disposisi yang belum selesai
      const response = await axios.get(
        `${API_ESA_OFFICE}disposisi/disposisi-penerima/${userId}/statistik`
      );
      setStatistik(response.data);
    } catch (error) {
      console.error("Error fetching statistik:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Render teks dengan link yang dapat diklik (buka tab baru)
  const renderTextWithLinks = (text) => {
    if (!text || typeof text !== "string") return null;
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
    const parts = text.split(urlRegex);
    return parts.map((part, i) => {
      const isUrl = part.match(/^https?:\/\//i) || part.match(/^www\./i);
      const href = isUrl && part.match(/^www\./i) ? `https://${part}` : part;
      return isUrl ? (
        <Link
          key={i}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: "primary.main", textDecoration: "underline" }}
        >
          {part}
        </Link>
      ) : (
        <span key={i}>{part}</span>
      );
    });
  };

  const handleOpenPenerimaDialog = (penerima) => {
    setSelectedPenerima(penerima);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPenerima(null);
  };

  const handleOpenPdfDialog = async (
    fileUrl,

    disposisiPenerimaId
  ) => {
    setSelectedFile(fileUrl);
    setOpenFileDialog(true);

    try {
      await axios.put(
        `${API_ESA_OFFICE}disposisi/lihat/${disposisiPenerimaId}`,
        {
          waktu_baca: new Date().toISOString(),
        }
      );
      fetchDisposisi();
    } catch (error) {
      console.error("Error updating waktu_baca:", error);
      swal({
        icon: "error",
        title: "Oops...",
        text: "Gagal memperbarui status pembacaan",
        button: "OK",
      });
    }
  };

  const handleCloseFileDialog = () => {
    setOpenFileDialog(false);
    setSelectedFile(null);
  };

  const handleOpenTindakLanjut = (disposisi) => {
    setSelectedDisposisi(disposisi);
    setOpenTindakLanjutDialog(true);
  };

  const handleCloseTindakLanjut = () => {
    setOpenTindakLanjutDialog(false);
    setSelectedDisposisi(null);
    setFiles([]);
    setTotalSize(0);
    setShowSizeWarning(false);
    setKeteranganTL("");
    setTanggalTL(new Date());
  };

  const handleDropzoneChange = (files) => {
    const totalSizeInMB =
      files.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024);
    setTotalSize(totalSizeInMB);
    setShowSizeWarning(totalSizeInMB > 3);
    setFiles(files);
  };

  const handleLanjutkanDropzoneChange = (files) => {
    const totalSizeInMB =
      files.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024);
    setTotalSize(totalSizeInMB);
    setShowSizeWarning(totalSizeInMB > 1); // 1 MB untuk lanjutkan
    setFiles(files);
  };

  const handleSubmitTindakLanjut = async () => {
    if (!selectedDisposisi || !keteranganTL) {
      swal({
        icon: "warning",
        title: "Peringatan",
        text: "Mohon lengkapi semua data yang diperlukan",
        button: "OK",
      });
      return;
    }

    // Validasi total ukuran file jika ada file
    if (files.length > 0) {
      const totalSizeInMB =
        files.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024);
      if (totalSizeInMB > 3) {
        swal({
          icon: "error",
          title: "Ukuran File Melebihi Batas",
          text: `Total ukuran file (${totalSizeInMB.toFixed(
            2
          )} MB) melebihi batas maksimal 3 MB`,
          button: "OK",
        });
        return;
      }
    }

    const formData = new FormData();
    formData.append("waktu_selesai", tanggalTL.toISOString().split("T")[0]);
    formData.append("keterangan_tl", keteranganTL);
    files.forEach((file) => {
      formData.append("file_TL_disposisi", file);
    });

    try {
      await axios.put(
        `${API_ESA_OFFICE}disposisi/tindak-lanjut/${selectedDisposisi.disposisi_penerima_id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      swal({
        icon: "success",
        title: "Berhasil!",
        text: "Tindak lanjut berhasil disimpan",
        button: "OK",
      });

      handleCloseTindakLanjut();
      fetchDisposisi();
      fetchStatistik();
    } catch (error) {
      console.error("Error submitting tindak lanjut:", error);
      swal({
        icon: "error",
        title: "Oops...",
        text: "Gagal menyimpan tindak lanjut",
        button: "OK",
      });
    }
  };

  const handleOpenDetailDialog = (disposisi) => {
    setSelectedDetail(disposisi);
    setOpenDetailDialog(true);
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setSelectedDetail(null);
  };

  const handleOpenLanjutkanDialog = (disposisi) => {
    setSelectedDisposisi(disposisi);
    setOpenLanjutkanDialog(true);
  };

  const handleCloseLanjutkanDialog = () => {
    setOpenLanjutkanDialog(false);
    setSelectedDisposisi(null);
    setSelectedPenerimaLanjut([]);
    setCatatanDisposisi("");
    setTingkatPerintah("");
    setFiles([]);
    setTotalSize(0);
    setShowSizeWarning(false);
  };

  const handleSubmitLanjutkan = async () => {
    if (
      selectedPenerimaLanjut.length === 0 ||
      !catatanDisposisi ||
      !tingkatPerintah
    ) {
      swal({
        icon: "warning",
        title: "Peringatan",
        text: "Mohon lengkapi semua data yang diperlukan",
        button: "OK",
      });
      return;
    }

    // Validasi total ukuran file jika ada file (1 MB untuk lanjutkan)
    if (files.length > 0) {
      const totalSizeInMB =
        files.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024);
      if (totalSizeInMB > 1) {
        swal({
          icon: "error",
          title: "Ukuran File Melebihi Batas",
          text: `Total ukuran file (${totalSizeInMB.toFixed(
            2
          )} MB) melebihi batas maksimal 1 MB`,
          button: "OK",
        });
        return;
      }
    }

    const formData = new FormData();
    formData.append("disposisi_id", selectedDisposisi.disposisi_id);
    formData.append("asal_perintah_dari_id", userId);
    formData.append("status", "Belum");
    formData.append("catatan_disposisi", catatanDisposisi);
    formData.append("tingkat_perintah", tingkatPerintah);

    const penerimaIds = selectedPenerimaLanjut.map((p) => p.value.toString());
    formData.append("penerima_ids", JSON.stringify(penerimaIds));
    files.forEach((file) => {
      formData.append("file_disposisi", file);
    });

    try {
      await axios.post(`${API_ESA_OFFICE}disposisi/lanjutkan`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      swal({
        icon: "success",
        title: "Berhasil!",
        text: "Disposisi berhasil dilanjutkan",
        button: "OK",
      });

      handleCloseLanjutkanDialog();
      fetchDisposisi();
      fetchStatistik();
    } catch (error) {
      console.error("Error submitting lanjutan disposisi:", error);
      swal({
        icon: "error",
        title: "Oops...",
        text: "Gagal melanjutkan disposisi",
        button: "OK",
      });
    }
  };

  const handleOpenPilihanDialog = (disposisi) => {
    setSelectedDisposisi(disposisi);
    setOpenPilihanDialog(true);
  };

  const handleClosePilihanDialog = () => {
    setOpenPilihanDialog(false);
    setSelectedDisposisi(null);
  };

  const handlePilihTindakLanjut = (tipe) => {
    handleClosePilihanDialog();
    if (tipe === "selesai") {
      handleOpenTindakLanjut(selectedDisposisi);
    } else {
      handleOpenLanjutkanDialog(selectedDisposisi);
    }
  };

  const handleOpenLanjutanDialog = (disposisi) => {
    setSelectedLanjutan(disposisi);
    setOpenLanjutanDialog(true);
  };

  const handleCloseLanjutanDialog = () => {
    setOpenLanjutanDialog(false);
    setSelectedLanjutan(null);
  };

  const handleOpenTindakLanjutPdfDialog = (fileUrl, disposisiPenerimaId) => {
    // Ubah fileUrl menjadi array jika bentuknya string array seperti '["/uploads/disposisi/xxx.pdf"]'
    if (
      typeof fileUrl === "string" &&
      fileUrl.startsWith("[") &&
      fileUrl.endsWith("]")
    ) {
      try {
        fileUrl = JSON.parse(fileUrl);
      } catch (e) {
        console.error("Gagal parse fileUrl ke array:", e);
        fileUrl = [];
      }
    }

    try {
      // Pastikan fileUrl adalah string dan tidak kosong
      if (!fileUrl) {
        throw new Error("URL file tidak valid");
      }

      // Jika fileUrl adalah array string, ambil elemen pertama
      let urlToUse = fileUrl;
      if (Array.isArray(fileUrl)) {
        urlToUse = fileUrl.length > 0 ? fileUrl[0] : "";
      }

      // Bersihkan URL dari karakter yang tidak diinginkan
      const cleanUrl = urlToUse.trim();

      // Pastikan URL file lengkap
      const fullUrl = cleanUrl.startsWith("http")
        ? cleanUrl
        : `${API_ESA_OFFICE_UPLOAD}${cleanUrl}`;

      setSelectedTindakLanjutPdf(fullUrl);
      setOpenTindakLanjutPdfDialog(true);
    } catch (error) {
      console.error("Error opening PDF:", error);
      swal({
        icon: "error",
        title: "Oops...",
        text: "Gagal membuka file PDF",
        button: "OK",
      });
    }
  };

  const handleCloseTindakLanjutPdfDialog = () => {
    setOpenTindakLanjutPdfDialog(false);
    setSelectedTindakLanjutPdf(null);
  };

  const getFreshToken = async (user) => {
    if (!user?.uid_RSS) {
      throw new Error("User uid_RSS tidak ditemukan");
    }

    try {
      const tokenResponse = await axios.get(
        `${API_ESA_OFFICE}users/uid/${user.uid_RSS}`,
        {
          withCredentials: true, // Kirim cookie untuk refresh token
        }
      );

      if (tokenResponse.data?.accessToken) {
        const token = tokenResponse.data.accessToken;
        // Simpan token ke localStorage
        const updatedUser = { ...user, accessToken: token };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        return token;
      }
      throw new Error("Access token tidak ditemukan di response");
    } catch (error) {
      console.error("Error getting fresh token:", error);

      // Handle error khusus untuk status_EO tidak aktif - logout tanpa dialog
      if (
        error.response &&
        error.response.status === 403 &&
        error.response.data?.status_EO != null
      ) {
        forceLogoutInactive();
        return;
      }

      throw error;
    }
  };

  // Fungsi untuk fetch chat history
  const fetchChatHistory = async (disposisiItem, token) => {
    const disposisiId = disposisiItem.disposisi_id;
    const disposisiPenerimaId = disposisiItem.disposisi_penerima_id || null;

    // Fetch chat history
    let response;
    try {
      const url = disposisiPenerimaId
        ? `${API_CHAT}disposisi/${disposisiId}?disposisi_penerima_id=${disposisiPenerimaId}`
        : `${API_CHAT}disposisi/${disposisiId}`;
      response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });
    } catch (error) {
      // Jika 403, coba refresh token sekali lagi
      if (error.response?.status === 403) {
        console.log("🔄 Token mungkin expired, refreshing...");
        try {
          const user = JSON.parse(localStorage.getItem("user")) || {};
          token = await getFreshToken(user);
          const url = disposisiPenerimaId
            ? `${API_CHAT}disposisi/${disposisiId}?disposisi_penerima_id=${disposisiPenerimaId}`
            : `${API_CHAT}disposisi/${disposisiId}`;
          response = await axios.get(url, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          });
        } catch (retryError) {
          console.error(
            "❌ Chat API Error after retry:",
            retryError.response?.data || retryError.message
          );
          throw retryError;
        }
      } else {
        throw error;
      }
    }

    // Set chat messages
    if (response && response.data && response.data.success) {
      const messages = response.data.data || [];
      setChatMessages(messages);

      // Auto mark all messages as read
      try {
        const user = JSON.parse(localStorage.getItem("user")) || {};
        const currentUserId =
          userId || user.uid_RSS || user.user_id || user.uid;
        const markReadUrl = disposisiPenerimaId
          ? `${API_CHAT}disposisi/${disposisiId}/read-all?disposisi_penerima_id=${disposisiPenerimaId}`
          : `${API_CHAT}disposisi/${disposisiId}/read-all`;
        const markReadResponse = await axios.put(
          markReadUrl,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );
        if (markReadResponse.data.success) {
          console.log("✅ All messages marked as read");
          setChatMessages((prev) =>
            prev.map((msg) => {
              if (!msg.waktu_baca && msg.penerima_id === currentUserId) {
                return { ...msg, waktu_baca: new Date().toISOString() };
              }
              return msg;
            })
          );
        }
      } catch (markReadError) {
        console.warn("⚠️ Failed to mark messages as read:", markReadError);
      }
    }

    return response;
  };

  // Handler untuk refresh chat messages
  const handleRefreshChat = async () => {
    if (!selectedChatDisposisi || refreshingChat) {
      return;
    }

    setRefreshingChat(true);
    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      if (!user.uid_RSS) {
        swal("Error", "Data user tidak lengkap. Silakan login ulang.", "error");
        setRefreshingChat(false);
        return;
      }

      const token = await getFreshToken(user);
      await fetchChatHistory(selectedChatDisposisi, token);

      // Scroll to bottom after refresh
      setTimeout(() => {
        if (chatEndRef.current) {
          chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);
    } catch (error) {
      console.error("Error refreshing chat:", error);
      swal("Error", "Gagal refresh chat. Silakan coba lagi.", "error");
    } finally {
      setRefreshingChat(false);
    }
  };

  // Handler untuk membuka chat
  const handleOpenChat = async (disposisiItem) => {
    setSelectedChatDisposisi(disposisiItem);
    setSelectedChatPenerima(disposisiItem.penerima);
    setOpenChatDialog(true);
    setLoadingChat(true);
    setChatMessages([]);
    setNewMessage("");

    try {
      // Ambil user dari localStorage
      const user = JSON.parse(localStorage.getItem("user")) || {};

      if (!user.uid_RSS) {
        swal("Error", "Data user tidak lengkap. Silakan login ulang.", "error");
        setLoadingChat(false);
        return;
      }

      // Selalu dapatkan fresh token untuk memastikan tidak expired
      let token;
      try {
        token = await getFreshToken(user);
        console.log("✅ Fresh token obtained");
      } catch (tokenError) {
        console.error("Error getting fresh token:", tokenError);
        swal(
          "Error",
          "Gagal mendapatkan token akses. Silakan login ulang.",
          "error"
        );
        setLoadingChat(false);
        return;
      }

      if (!token) {
        swal("Error", "Token tidak ditemukan. Silakan login ulang.", "error");
        setLoadingChat(false);
        return;
      }

      // Fetch chat history
      try {
        await fetchChatHistory(disposisiItem, token);
        // Scroll to bottom
        setTimeout(() => {
          if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 150);
      } catch (error) {
        console.error("Error loading chat:", error);
        if (error.response?.status >= 400) {
          swal(
            "Error",
            error.response?.data?.error || "Gagal memuat chat",
            "error"
          );
        }
        setChatMessages([]);
      }
    } catch (error) {
      console.error("Error loading chat:", error);
      swal("Error", "Gagal memuat chat. Silakan coba lagi.", "error");
    } finally {
      setLoadingChat(false);
    }
  };

  // Handler untuk menutup chat
  const handleCloseChat = () => {
    setOpenChatDialog(false);
    setSelectedChatDisposisi(null);
    setSelectedChatPenerima(null);
    setChatMessages([]);
    setNewMessage("");
  };

  // Handler untuk mengirim pesan
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChatDisposisi) {
      return;
    }

    const messageText = newMessage.trim();
    setNewMessage("");

    const currentUserId =
      userId ||
      JSON.parse(localStorage.getItem("user"))?.uid ||
      JSON.parse(localStorage.getItem("user"))?.user_id;
    const tempId = `temp-${Date.now()}-${Math.random()}`;

    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};

      if (!user.uid_RSS) {
        swal("Error", "Data user tidak lengkap. Silakan login ulang.", "error");
        setNewMessage(messageText); // Restore message on error
        return;
      }

      // Dapatkan fresh token
      let token;
      try {
        token = await getFreshToken(user);
      } catch (tokenError) {
        console.error("Error getting fresh token:", tokenError);
        swal(
          "Error",
          "Gagal mendapatkan token akses. Silakan login ulang.",
          "error"
        );
        setNewMessage(messageText); // Restore message on error
        return;
      }

      if (!token) {
        swal("Error", "Token tidak ditemukan. Silakan login ulang.", "error");
        setNewMessage(messageText); // Restore message on error
        return;
      }

      const tempMessage = {
        id_chat: tempId,
        pesan: messageText,
        pengirim_id: currentUserId,
        pengirim_nama: user.nama || "Anda",
        pengirim_profil: user.uid_RSS || user.uid_RSS,
        waktu_kirim: new Date().toISOString(),
        isSending: true,
      };

      setChatMessages((prev) => {
        const exists = prev.some((msg) => msg.id_chat === tempId);
        if (exists) return prev;
        return [...prev, tempMessage];
      });

      setTimeout(() => {
        if (chatEndRef.current) {
          chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);

      // Di disposisi-TL, user adalah penerima disposisi, jadi penerima_id adalah pengirim disposisi
      // Pengirim disposisi adalah yang ada di asal_perintah_dari_id
      const penerimaId = selectedChatDisposisi.asal_perintah_dari_id;

      if (!penerimaId) {
        swal("Error", "Data pengirim disposisi tidak ditemukan.", "error");
        setNewMessage(messageText); // Restore message on error
        return;
      }

      if (!selectedChatDisposisi.disposisi_id) {
        swal("Error", "Data disposisi tidak lengkap.", "error");
        setNewMessage(messageText); // Restore message on error
        return;
      }

      const messageData = {
        disposisi_id: selectedChatDisposisi.disposisi_id,
        disposisi_penerima_id:
          selectedChatDisposisi.disposisi_penerima_id || null,
        penerima_id: penerimaId,
        pesan: messageText,
      };

      const response = await axios.post(`${API_CHAT}disposisi`, messageData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (response.data && response.data.success) {
        // Hapus pesan temporary
        setChatMessages((prev) => prev.filter((msg) => msg.id_chat !== tempId));

        // Refresh chat untuk mendapatkan pesan terbaru
        await fetchChatHistory(selectedChatDisposisi, token);

        // Scroll to bottom
        setTimeout(() => {
          if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 150);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setChatMessages((prev) => prev.filter((msg) => msg.id_chat !== tempId));
      setNewMessage(messageText);
      swal(
        "Error",
        error.response?.data?.error || "Gagal mengirim pesan",
        "error"
      );
    }
  };

  // Handler untuk delete message
  const handleDeleteMessage = async (chatId) => {
    if (!chatId) return;

    const confirmed = await swal({
      title: "Hapus Pesan?",
      text: "Pesan yang dihapus tidak dapat dikembalikan.",
      icon: "warning",
      buttons: {
        cancel: {
          text: "Batal",
          value: false,
          visible: true,
        },
        confirm: {
          text: "Hapus",
          value: true,
          visible: true,
        },
      },
      dangerMode: true,
    });

    if (!confirmed) return;

    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      const token = await getFreshToken(user);

      await axios.delete(`${API_CHAT}disposisi/${chatId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      setChatMessages((prev) => prev.filter((msg) => msg.id_chat !== chatId));
      swal("Berhasil", "Pesan berhasil dihapus", "success");
    } catch (error) {
      console.error("Error deleting message:", error);
      swal("Error", "Gagal menghapus pesan", "error");
    }
  };

  // Format waktu untuk chat
  const formatChatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);

    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const timeStr = `${hours}:${minutes}`;

    if (diffDays === 0) {
      return timeStr;
    }

    if (diffDays === 1) {
      return `Kemarin ${timeStr}`;
    }

    return `${date.getDate()}/${date.getMonth() + 1} ${timeStr}`;
  };

  const renderStatusButton = (disposisi) => {
    const isCompleted = disposisi.penerima.waktu_selesai;
    return (
      <Button
        variant="contained"
        size="small"
        color={isCompleted ? "success" : "warning"}
        startIcon={isCompleted ? <CheckCircleIcon /> : <CancelIcon />}
        onClick={() => handleOpenDetailDialog(disposisi)}
        sx={{
          backgroundColor: isCompleted ? "#2e7d32" : "#ed6c02",
          color: "#ffffff",
          "&:hover": {
            backgroundColor: isCompleted ? "#1b5e20" : "#e65100",
            color: "#ffffff",
          },
        }}
      >
        {isCompleted ? "Selesai" : "Belum Selesai"}
      </Button>
    );
  };

  const renderPenerima = (penerima) => {
    if (!penerima || penerima.length === 0) {
      return <MDTypography variant="caption">Tidak ada penerima</MDTypography>;
    }

    if (penerima.length === 1) {
      return (
        <MDBox display="flex" alignItems="center">
          <MDAvatar
            src={penerima[0].profil}
            alt={penerima[0].nama}
            size="sm"
            sx={{ mr: 1 }}
          />
          <MDTypography variant="button" fontWeight="medium">
            {penerima[0].nama}
          </MDTypography>

          <MDBox display="flex" alignItems="center">
            <img
              src={penerima[0].profil}
              alt={penerima[0].nama}
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid #e0e0e0",
              }}
              onError={(e) => {
                // Jika gambar tidak ditemukan, tampilkan icon default
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
            <MDBox
              display="none"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "grey.200",
                color: "grey.500",
              }}
            >
              <Icon sx={{ fontSize: 20 }}>person</Icon>
            </MDBox>
          </MDBox>
        </MDBox>
      );
    }

    return (
      <Button
        variant="contained"
        size="small"
        onClick={() => handleOpenPenerimaDialog(penerima)}
      >
        {penerima.length} Penerima
      </Button>
    );
  };

  // Render rating (bintang) - tampil jika penerima sudah diberi rating
  const renderRating = (disposisi, isMobile = false) => {
    const nilai = disposisi?.penerima?.nilai;
    if (!nilai || nilai === "" || Number(nilai) <= 0) {
      return (
        <MDTypography variant="caption" color="text.secondary">
          -
        </MDTypography>
      );
    }

    const numNilai = Number(nilai);
    const starSize = isMobile ? "1.25rem" : "1rem";

    return (
      <MDBox
        display="flex"
        alignItems="center"
        gap={isMobile ? 0.5 : 0.25}
        flexWrap="wrap"
      >
        {[1, 2, 3, 4, 5].map((star) => (
          <Icon
            key={star}
            sx={{
              color: star <= numNilai ? "#FFD700" : "#E0E0E0",
              fontSize: starSize,
            }}
          >
            star
          </Icon>
        ))}
        <MDTypography
          variant="caption"
          color="text.secondary"
          sx={{ ml: isMobile ? 1 : 0.5, fontSize: isMobile ? "0.875rem" : undefined }}
        >
          ({numNilai}/5)
        </MDTypography>
      </MDBox>
    );
  };

  const renderFileButton = (files, disposisiPenerimaId) => {
    if (!files || files.length === 0) {
      return <MDTypography variant="caption">Tidak ada file</MDTypography>;
    }

    return (
      <Button
        variant="contained"
        size="small"
        startIcon={<VisibilityIcon />}
        onClick={() => handleOpenPdfDialog(files[0], disposisiPenerimaId)}
      >
        Lihat ({files.length})
      </Button>
    );
  };

  const renderTindakLanjutButton = (disposisi) => {
    if (!disposisi.penerima.waktu_selesai) {
      return (
        <Button
          variant="contained"
          color="success"
          size="small"
          onClick={() => handleOpenTindakLanjut(disposisi)}
        >
          Tindak Lanjut
        </Button>
      );
    }
    return null;
  };

  const renderAksi = (row) => {
    return (
      <MDBox display="flex" gap={1}>
        {!row.penerima.waktu_selesai && (
          <Button
            variant="outlined"
            color="info"
            size="small"
            onClick={() => handleOpenPilihanDialog(row)}
            startIcon={<SendIcon />}
            sx={{
              backgroundColor: "info.main",
              "&:hover": {
                backgroundColor: "info.dark",
              },
            }}
          >
            Tindak Lanjut
          </Button>
        )}
      </MDBox>
    );
  };

  // Fungsi untuk mendapatkan icon berdasarkan jenis file
  const getFileIcon = (filename) => {
    if (!filename) return <Icon>insert_drive_file</Icon>;

    const extension = filename.split(".").pop()?.toLowerCase();

    switch (extension) {
      case "pdf":
        return <Icon sx={{ color: "#f44336" }}>picture_as_pdf</Icon>;
      case "doc":
      case "docx":
        return <Icon sx={{ color: "#2196f3" }}>description</Icon>;
      case "xls":
      case "xlsx":
        return <Icon sx={{ color: "#4caf50" }}>table_chart</Icon>;
      case "ppt":
      case "pptx":
        return <Icon sx={{ color: "#ff9800" }}>slideshow</Icon>;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <Icon sx={{ color: "#9c27b0" }}>image</Icon>;
      case "txt":
        return <Icon sx={{ color: "#607d8b" }}>text_snippet</Icon>;
      default:
        return <Icon>insert_drive_file</Icon>;
    }
  };

  const renderLanjutan = (disposisi) => {
    if (
      !disposisi.disposisi_lanjutan ||
      disposisi.disposisi_lanjutan.length === 0
    ) {
      return <MDTypography variant="caption">Tidak ada lanjutan</MDTypography>;
    }

    return (
      <Button
        variant="contained"
        size="small"
        onClick={() => handleOpenLanjutanDialog(disposisi)}
        startIcon={<VisibilityIcon />}
      >
        Detail ({disposisi.disposisi_lanjutan.length})
      </Button>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={isMobile ? 4 : 6} pb={3} px={isMobile ? 1 : 0}>
        <Grid container spacing={isMobile ? 3 : 6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={isMobile ? 1 : 2}
                mt={-3}
                py={3}
                px={isMobile ? 1.5 : 2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: isMobile ? "1rem" : undefined,
                    wordBreak: "break-word",
                  }}
                >
                  Daftar Disposisi Tindak Lanjut (Belum Selesai)
                </MDTypography>
              </MDBox>
              <MDBox pt={4} pb={3} px={isMobile ? 2 : 3}>
                {/* Statistik - Chips filter (desktop) / Autocomplete (mobile) */}
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12}>
                    <MDBox
                      display="flex"
                      gap={2}
                      flexWrap="wrap"
                      alignItems="center"
                      height="100%"
                      flexDirection={isMobile ? "column" : "row"}
                      px={isMobile ? 0 : undefined}
                    >
                      {isMobile ? (
                        <>
                          <Autocomplete
                            options={[
                              {
                                value: "belum_selesai",
                                label: `Belum Selesai: ${statistik.total_belum_selesai || 0}`,
                              },
                              {
                                value: "all",
                                label: `Total: ${statistik.total_disposisi || 0}`,
                              },
                              {
                                value: "proses",
                                label: `Proses: ${statistik.total_proses || 0}`,
                              },
                              {
                                value: "selesai",
                                label: `Selesai: ${statistik.total_selesai || 0}`,
                              },
                              {
                                value: "belum_dibaca",
                                label: `Belum Dibaca: ${statistik.total_belum_dibaca || 0}`,
                              },
                            ]}
                            value={
                              [
                                { value: "belum_selesai", label: `Belum Selesai: ${statistik.total_belum_selesai || 0}` },
                                { value: "all", label: `Total: ${statistik.total_disposisi || 0}` },
                                { value: "proses", label: `Proses: ${statistik.total_proses || 0}` },
                                { value: "selesai", label: `Selesai: ${statistik.total_selesai || 0}` },
                                { value: "belum_dibaca", label: `Belum Dibaca: ${statistik.total_belum_dibaca || 0}` },
                              ].find((o) => o.value === activeFilter) ?? null
                            }
                            onChange={(event, newValue) => {
                              if (newValue) setActiveFilter(newValue.value);
                            }}
                            getOptionLabel={(option) =>
                              typeof option === "string" ? option : option.label
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.value === value?.value
                            }
                            sx={{ width: "100%" }}
                            renderInput={(params) => (
                              <MDInput
                                {...params}
                                label="Filter"
                                variant="standard"
                                size="small"
                              />
                            )}
                          />
                          {(activeFilter === "all" ||
                            activeFilter === "selesai") && (
                            <Autocomplete
                              options={(() => {
                                const currentYear =
                                  new Date().getFullYear();
                                const years = [];
                                for (let i = 2025; i <= currentYear; i++) {
                                  years.push({
                                    label: i.toString(),
                                    value: i,
                                  });
                                }
                                return years.reverse();
                              })()}
                              value={
                                tahunFilter
                                  ? {
                                      label: tahunFilter.toString(),
                                      value: tahunFilter,
                                    }
                                  : null
                              }
                              onChange={(event, newValue) => {
                                if (newValue) {
                                  setTahunFilter(newValue.value);
                                }
                              }}
                              sx={{ minWidth: "100%" }}
                              renderInput={(params) => (
                                <MDInput
                                  {...params}
                                  label="Tahun"
                                  variant="standard"
                                  size="small"
                                />
                              )}
                            />
                          )}
                        </>
                      ) : (
                        <>
                          <Chip
                            label={`Belum Selesai: ${
                              statistik.total_belum_selesai || 0
                            }`}
                            color="secondary"
                            size="medium"
                            onClick={() => setActiveFilter("belum_selesai")}
                            variant={
                              activeFilter === "belum_selesai"
                                ? "filled"
                                : "outlined"
                            }
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: "medium",
                              cursor: "pointer",
                            }}
                          />
                          <Chip
                            label={`Total: ${statistik.total_disposisi || 0}`}
                            color="info"
                            size="medium"
                            onClick={() => setActiveFilter("all")}
                            variant={
                              activeFilter === "all"
                                ? "filled"
                                : "outlined"
                            }
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: "medium",
                              cursor: "pointer",
                            }}
                          />
                          <Chip
                            label={`Proses: ${statistik.total_proses || 0}`}
                            color="warning"
                            size="medium"
                            onClick={() => setActiveFilter("proses")}
                            variant={
                              activeFilter === "proses"
                                ? "filled"
                                : "outlined"
                            }
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: "medium",
                              cursor: "pointer",
                            }}
                          />
                          <Chip
                            label={`Selesai: ${statistik.total_selesai || 0}`}
                            color="success"
                            size="medium"
                            onClick={() => setActiveFilter("selesai")}
                            variant={
                              activeFilter === "selesai"
                                ? "filled"
                                : "outlined"
                            }
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: "medium",
                              cursor: "pointer",
                            }}
                          />
                          <Chip
                            label={`Belum Dibaca: ${
                              statistik.total_belum_dibaca || 0
                            }`}
                            color="error"
                            size="medium"
                            onClick={() => setActiveFilter("belum_dibaca")}
                            variant={
                              activeFilter === "belum_dibaca"
                                ? "filled"
                                : "outlined"
                            }
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: "medium",
                              cursor: "pointer",
                            }}
                          />
                          {(activeFilter === "all" ||
                            activeFilter === "selesai") && (
                            <Autocomplete
                              options={(() => {
                                const currentYear =
                                  new Date().getFullYear();
                                const years = [];
                                for (let i = 2025; i <= currentYear; i++) {
                                  years.push({
                                    label: i.toString(),
                                    value: i,
                                  });
                                }
                                return years.reverse();
                              })()}
                              value={
                                tahunFilter
                                  ? {
                                      label: tahunFilter.toString(),
                                      value: tahunFilter,
                                    }
                                  : null
                              }
                              onChange={(event, newValue) => {
                                if (newValue) {
                                  setTahunFilter(newValue.value);
                                }
                              }}
                              sx={{ minWidth: 120 }}
                              renderInput={(params) => (
                                <MDInput
                                  {...params}
                                  label="Tahun"
                                  variant="standard"
                                  size="small"
                                />
                              )}
                            />
                          )}
                        </>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
                {isMobile ? (
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    px={1}
                    sx={{ overflow: "hidden" }}
                  >
                    {disposisiList.map((disposisi, index) => (
                      <Card key={disposisi.disposisi_penerima_id || index}>
                        <CardContent sx={{ px: 2, py: 2, "&:last-child": { pb: 2 } }}>
                          <MDBox mb={1}>
                            <MDTypography variant="h6" fontWeight="bold">
                              {disposisi.nomor_surat || "-"}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatDate(disposisi.tanggal_disposisi)}
                            </MDTypography>
                          </MDBox>
                          <MDBox
                            display="flex"
                            alignItems="center"
                            mb={1}
                            sx={{ minWidth: 0, overflow: "hidden" }}
                          >
                            <MDAvatar
                              src={disposisi.profil_pengirim}
                              alt={disposisi.nama_pengirim}
                              size="sm"
                              sx={{ mr: 1, flexShrink: 0 }}
                            />
                            <MDBox sx={{ flex: 1, minWidth: 0 }}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="medium"
                              >
                                Pemberi Instruksi:
                              </MDTypography>
                              <MDBox
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{ flexWrap: "wrap" }}
                              >
                                <MDTypography
                                  variant="body2"
                                  color="text"
                                  sx={{
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                  }}
                                >
                                  {disposisi.nama_pengirim || "-"}
                                </MDTypography>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenChat(disposisi);
                                  }}
                                  sx={{ ml: 0.5, flexShrink: 0 }}
                                  title="Chat dengan pemberi instruksi"
                                >
                                  <ChatIcon fontSize="small" />
                                </IconButton>
                              </MDBox>
                            </MDBox>
                          </MDBox>
                          <MDBox mb={1} sx={{ minWidth: 0, overflow: "hidden" }}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Perihal:
                            </MDTypography>
                            <MDTypography
                              variant="body2"
                              color="text"
                              sx={{
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {disposisi.perihal || "-"}
                            </MDTypography>
                          </MDBox>
                          <MDBox mb={1} sx={{ minWidth: 0, overflow: "hidden" }}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Catatan:
                            </MDTypography>
                            <MDTypography
                              variant="body2"
                              color="text"
                              sx={{
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {disposisi.catatan_disposisi || "-"}
                            </MDTypography>
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                              mb={0.5}
                            >
                              Status:
                            </MDTypography>
                            <MDBox display="flex" alignItems="center" mb={1}>
                              {disposisi.penerima?.waktu_baca ? (
                                <>
                                  <VisibilityIcon
                                    sx={{
                                      color: "success.main",
                                      mr: 1,
                                      fontSize: 18,
                                    }}
                                  />
                                  <MDTypography
                                    variant="caption"
                                    color="success"
                                  >
                                    Dibaca:{" "}
                                    {formatDate(disposisi.penerima.waktu_baca)}
                                  </MDTypography>
                                </>
                              ) : (
                                <>
                                  <VisibilityOffIcon
                                    sx={{
                                      color: "error.main",
                                      mr: 1,
                                      fontSize: 18,
                                    }}
                                  />
                                  <MDTypography variant="caption" color="error">
                                    Belum dibaca
                                  </MDTypography>
                                </>
                              )}
                            </MDBox>
                            {renderStatusButton(disposisi)}
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                              mb={0.5}
                            >
                              Rating:
                            </MDTypography>
                            {renderRating(disposisi, true)}
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                              mb={0.5}
                            >
                              File Surat:
                            </MDTypography>
                            {renderFileButton(
                              [disposisi.file_surat],
                              disposisi.disposisi_penerima_id
                            )}
                          </MDBox>
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                              mb={0.5}
                            >
                              File Disposisi:
                            </MDTypography>
                            {renderFileButton(
                              disposisi.penerima?.file_disposisi,
                              disposisi.disposisi_penerima_id
                            )}
                          </MDBox>
                          <MDBox
                            display="flex"
                            gap={1}
                            flexWrap="wrap"
                            mt={2}
                            sx={{
                              flexDirection: isMobile ? "column" : "row",
                              "& > *": isMobile
                                ? { width: "100%", minWidth: "100%" }
                                : {},
                              "& .MuiButton-root": isMobile
                                ? { width: "100%", justifyContent: "center" }
                                : {},
                            }}
                          >
                            {renderAksi(disposisi)}
                            {renderLanjutan(disposisi)}
                          </MDBox>
                        </CardContent>
                      </Card>
                    ))}
                    {disposisiList.length === 0 && !loading && (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        py={4}
                      >
                        <MDTypography variant="h6" color="text.secondary">
                          Tidak ada data disposisi tindak lanjut
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                ) : (
                  <DataTable
                    table={{
                      columns: [
                        {
                          Header: "No",
                          accessor: "no",
                          width: "5%",
                          Cell: ({ row }) => (
                            <MDTypography variant="caption" color="text">
                              {row.index + 1}
                            </MDTypography>
                          ),
                        },
                        {
                          Header: "Detail",
                          accessor: "detail",
                          width: "5%",
                          Cell: ({ row }) => {
                            const disposisiId = row.original.disposisi_id;
                            if (!disposisiId) return null;
                            return (
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  navigate(
                                    `/task-management/disposisi/disposisi-detail/${disposisiId}`
                                  )
                                }
                                title="Lihat Detail"
                                sx={{ cursor: "pointer" }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            );
                          },
                        },
                        {
                          Header: "No. Surat / File",
                          accessor: "nomor_surat",
                          width: "12%",
                          Cell: ({ row }) => {
                            const { nomor_surat, file_surat, disposisi_penerima_id } = row.original;
                            return (
                              <MDBox display="flex" flexDirection="column" gap={0.5}>
                                <MDTypography variant="caption" color="text" fontWeight="medium">
                                  {nomor_surat || "-"}
                                </MDTypography>
                                {file_surat &&
                                  renderFileButton(
                                    [file_surat],
                                    disposisi_penerima_id
                                  )}
                              </MDBox>
                            );
                          },
                        },
                        {
                          Header: "Pemberi / Tanggal",
                          accessor: "nama_pengirim",
                          width: "14%",
                          Cell: ({ row }) => {
                            const { nama_pengirim, profil_pengirim, tanggal_disposisi } = row.original;
                            return (
                              <MDBox display="flex" flexDirection="column" gap={0.5}>
                                <MDBox display="flex" alignItems="center">
                                  <MDAvatar
                                    src={profil_pengirim}
                                    alt={nama_pengirim}
                                    size="sm"
                                    sx={{ mr: 1 }}
                                  />
                                  <MDBox display="flex" alignItems="center" gap={1}>
                                    <MDTypography variant="button" fontWeight="medium">
                                      {nama_pengirim || "-"}
                                    </MDTypography>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenChat(row.original);
                                      }}
                                      sx={{ ml: 0.5 }}
                                      title="Chat dengan pemberi instruksi"
                                    >
                                      <ChatIcon fontSize="small" />
                                    </IconButton>
                                  </MDBox>
                                </MDBox>
                                <MDTypography variant="caption" color="text.secondary">
                                  {formatDate(tanggal_disposisi)}
                                </MDTypography>
                              </MDBox>
                            );
                          },
                        },
                        {
                          Header: "Perihal / Catatan",
                          accessor: "perihal",
                          width: "260px",
                          Cell: ({ row }) => {
                            const { perihal, catatan_disposisi } = row.original;
                            return (
                              <MDBox
                                sx={{
                                  whiteSpace: "normal",
                                  wordWrap: "break-word",
                                  maxWidth: "260px",
                                  py: 1,
                                }}
                              >
                                <MDTypography variant="caption" color="text" fontWeight="medium" display="block">
                                  {perihal || "-"}
                                </MDTypography>
                                <MDTypography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                  {catatan_disposisi || "-"}
                                </MDTypography>
                              </MDBox>
                            );
                          },
                        },
                        {
                          Header: "File Disposisi",
                          accessor: "file_disposisi",
                          width: "10%",
                          Cell: ({ row }) =>
                            renderFileButton(
                              row.original.penerima.file_disposisi,
                              row.original.disposisi_penerima_id
                            ),
                        },
                        {
                          Header: "Status / Rating",
                          accessor: "status_disposisi",
                          width: "14%",
                          Cell: ({ row }) => (
                            <MDBox display="flex" flexDirection="column" gap={1}>
                              <MDBox>
                                <MDBox display="flex" alignItems="center" mb={1}>
                                  {row.original.penerima.waktu_baca ? (
                                    <>
                                      <VisibilityIcon
                                        sx={{ color: "success.main", mr: 1 }}
                                      />
                                      <MDTypography variant="caption" color="success">
                                        Dibaca:{" "}
                                        {formatDate(row.original.penerima.waktu_baca)}
                                      </MDTypography>
                                    </>
                                  ) : (
                                    <>
                                      <VisibilityOffIcon
                                        sx={{ color: "error.main", mr: 1 }}
                                      />
                                      <MDTypography variant="caption" color="error">
                                        Belum dibaca
                                      </MDTypography>
                                    </>
                                  )}
                                </MDBox>
                                {renderStatusButton(row.original)}
                              </MDBox>
                              {renderRating(row.original)}
                            </MDBox>
                          ),
                        },
                        {
                          Header: "Aksi",
                          accessor: "aksi",
                          width: "15%",
                          Cell: ({ row }) => renderAksi(row.original),
                        },
                        {
                          Header: "Lanjutkan",
                          accessor: "disposisi_lanjutan",
                          width: "15%",
                          Cell: ({ row }) => renderLanjutan(row.original),
                        },
                      ],
                      rows: disposisiList,
                    }}
                    entriesPerPage={{
                      defaultValue: 10,
                      entries: [5, 10, 15, 20, 25],
                    }}
                    showTotalEntries={true}
                    isSorted={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog untuk menampilkan daftar penerima */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Daftar Penerima Disposisi</DialogTitle>
        <DialogContent>
          <List>
            {selectedPenerima?.map((penerima) => (
              <ListItem key={penerima.penerima_id}>
                <ListItemAvatar>
                  <MDAvatar src={penerima.profil} alt={penerima.nama} />
                </ListItemAvatar>
                <ListItemText
                  primary={penerima.nama}
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      Status: {penerima.status || "Belum dibaca"}
                      {penerima.waktu_baca && (
                        <span>
                          {" "}
                          • Dibaca: {formatDate(penerima.waktu_baca)}
                        </span>
                      )}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Tutup</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog untuk menampilkan file PDF */}
      <Dialog
        open={openFileDialog}
        onClose={handleCloseFileDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>File Surat</DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {selectedFile && (
              <iframe
                src={selectedFile}
                width="100%"
                height="600px"
                title="PDF Viewer"
                style={{ border: "none" }}
              />
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileDialog}>Tutup</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog untuk Tindak Lanjut */}
      <Dialog
        open={openTindakLanjutDialog}
        onClose={handleCloseTindakLanjut}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Tindak Lanjut Disposisi</DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={isMobile ? 2 : 3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDTypography variant="h6">Detail Disposisi</MDTypography>
                <MDBox mt={2}>
                  <MDTypography variant="body2">
                    <strong>No. Surat:</strong> {selectedDisposisi?.nomor_surat}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Perihal:</strong> {selectedDisposisi?.perihal}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Catatan:</strong>{" "}
                    {selectedDisposisi?.catatan_disposisi}
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <MDDatePicker
                  input={{
                    label: "Tanggal Tindak Lanjut",
                    variant: "standard",
                    fullWidth: true,
                  }}
                  value={tanggalTL}
                  onChange={(date) => setTanggalTL(date)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Keterangan Tindak Lanjut"
                  multiline
                  rows={4}
                  fullWidth
                  value={keteranganTL}
                  onChange={(e) => setKeteranganTL(e.target.value)}
                  variant="standard"
                />
              </Grid>
              <Grid item xs={12}>
                <MDTypography variant="h6">
                  Upload File Tindak Lanjut
                </MDTypography>
                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontWeight="regular"
                  >
                    * Maksimal total ukuran file: 3MB
                    <br />* Format yang didukung: PDF, DOC, DOCX, XLS, XLSX,
                    JPG, JPEG
                  </MDTypography>
                </MDBox>
                {useMemo(
                  () => (
                    <MDDropzone
                      options={dropzoneOptionsTindakLanjut}
                      onChange={handleDropzoneChange}
                      ref={dropzoneRef}
                    />
                  ),
                  [dropzoneOptionsTindakLanjut]
                )}
                {showSizeWarning && (
                  <MDBox mt={2}>
                    <MDTypography
                      variant="caption"
                      color="error"
                      fontWeight="regular"
                    >
                      ⚠️ Total ukuran file ({totalSize.toFixed(2)} MB) melebihi
                      batas maksimal (3 MB)
                    </MDTypography>
                  </MDBox>
                )}
                <MDBox mt={1}>
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontWeight="regular"
                  >
                    Total ukuran: {totalSize.toFixed(2)} MB / 3 MB
                  </MDTypography>
                </MDBox>
                {files.length > 0 && (
                  <MDBox mt={2}>
                    <MDTypography variant="subtitle2" fontWeight="medium">
                      File yang akan diunggah:
                    </MDTypography>
                    <List>
                      {files.map((file, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={file.name}
                            secondary={`${(file.size / (1024 * 1024)).toFixed(
                              2
                            )} MB`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </MDBox>
                )}
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTindakLanjut}>Batal</Button>
          <Button
            onClick={handleSubmitTindakLanjut}
            variant="contained"
            color="success"
            disabled={showSizeWarning || !keteranganTL}
          >
            Simpan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Detail Disposisi */}
      <Dialog
        open={openDetailDialog}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center">
            <InfoIcon sx={{ mr: 1 }} />
            Detail Disposisi dan Tindak Lanjut
          </MDBox>
        </DialogTitle>
        <DialogContent>
          {selectedDetail && (
            <MDBox pt={2} pb={3} px={isMobile ? 2 : 3}>
              <Grid container spacing={3}>
                {/* Informasi Surat */}
                <Grid item xs={12}>
                  <MDTypography variant="h6" mb={2}>
                    Informasi Surat
                  </MDTypography>
                  <MDBox>
                    <MDTypography variant="body2">
                      <strong>No. Surat:</strong> {selectedDetail.nomor_surat}
                    </MDTypography>
                    <MDTypography variant="body2">
                      <strong>Tanggal Surat:</strong>{" "}
                      {formatDate(selectedDetail.tanggal_surat)}
                    </MDTypography>
                    <MDTypography variant="body2">
                      <strong>Pengirim Surat:</strong>{" "}
                      {selectedDetail.pengirim_surat}
                    </MDTypography>
                    <MDTypography variant="body2">
                      <strong>Perihal:</strong> {selectedDetail.perihal}
                    </MDTypography>
                  </MDBox>
                </Grid>

                {/* Informasi Disposisi */}
                <Grid item xs={12}>
                  <MDTypography variant="h6" mb={2}>
                    Informasi Disposisi
                  </MDTypography>
                  <MDBox>
                    <MDTypography variant="body2">
                      <strong>Tanggal Disposisi:</strong>{" "}
                      {formatDate(selectedDetail.tanggal_disposisi)}
                    </MDTypography>
                    <MDTypography variant="body2">
                      <strong>Pemberi Instruksi:</strong>{" "}
                      {selectedDetail.nama_pengirim}
                    </MDTypography>
                    <MDTypography variant="body2">
                      <strong>Catatan Disposisi:</strong>{" "}
                      {selectedDetail.catatan_disposisi}
                    </MDTypography>
                  </MDBox>
                </Grid>

                {/* Status dan Tindak Lanjut */}
                <Grid item xs={12}>
                  <MDTypography variant="h6" mb={2}>
                    Status dan Tindak Lanjut
                  </MDTypography>
                  <MDBox>
                    <MDTypography variant="body2">
                      <strong>Status Pembacaan:</strong>{" "}
                      {selectedDetail.penerima.waktu_baca ? (
                        <MDTypography component="span" color="success">
                          Dibaca pada{" "}
                          {formatDate(selectedDetail.penerima.waktu_baca)}
                        </MDTypography>
                      ) : (
                        <MDTypography component="span" color="error">
                          Belum dibaca
                        </MDTypography>
                      )}
                    </MDTypography>
                    <MDTypography variant="body2">
                      <strong>Status Penyelesaian:</strong>{" "}
                      {selectedDetail.penerima.waktu_selesai ? (
                        <MDTypography component="span" color="success">
                          Selesai pada{" "}
                          {formatDate(selectedDetail.penerima.waktu_selesai)}
                        </MDTypography>
                      ) : (
                        <MDTypography component="span" color="error">
                          Belum selesai
                        </MDTypography>
                      )}
                    </MDTypography>
                    {selectedDetail.penerima.keterangan_tl && (
                      <MDTypography
                        variant="body2"
                        component="span"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        <strong>Keterangan Tindak Lanjut:</strong>{" "}
                        {renderTextWithLinks(
                          selectedDetail.penerima.keterangan_tl
                        )}
                      </MDTypography>
                    )}
                  </MDBox>
                </Grid>

                {/* File Lampiran */}
                <Grid item xs={12}>
                  <MDTypography variant="h6" mb={2}>
                    File Lampiran
                  </MDTypography>
                  <MDBox>
                    <MDTypography variant="subtitle2" mb={1}>
                      File Disposisi:
                    </MDTypography>
                    {selectedDetail.penerima.file_disposisi &&
                      selectedDetail.penerima.file_disposisi.map(
                        (file, index) => (
                          <Button
                            key={index}
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => {
                              handleOpenTindakLanjutPdfDialog(
                                file,
                                selectedDetail.disposisi_penerima_id
                              );
                            }}
                            sx={{ mr: 1, mb: 1 }}
                          >
                            File {index + 1}
                          </Button>
                        )
                      )}

                    {selectedDetail.penerima.file_TL_disposisi &&
                      selectedDetail.penerima.file_TL_disposisi.length > 0 && (
                        <>
                          <MDTypography variant="subtitle2" mt={2} mb={1}>
                            File Tindak Lanjut:
                          </MDTypography>
                          {selectedDetail.penerima.file_TL_disposisi.map(
                            (file, index) => (
                              <Button
                                key={index}
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() =>
                                  handleOpenTindakLanjutPdfDialog(
                                    file,
                                    selectedDetail.disposisi_penerima_id
                                  )
                                }
                                sx={{ mr: 1, mb: 1 }}
                              >
                                File {index + 1}
                              </Button>
                            )
                          )}
                        </>
                      )}
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailDialog}>Tutup</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog untuk Melanjutkan Disposisi */}
      <Dialog
        open={openLanjutkanDialog}
        onClose={handleCloseLanjutkanDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Lanjutkan Disposisi</DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={isMobile ? 2 : 3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDTypography variant="h6">Detail Disposisi</MDTypography>
                <MDBox mt={2}>
                  <MDTypography variant="body2">
                    <strong>No. Surat:</strong> {selectedDisposisi?.nomor_surat}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Perihal:</strong> {selectedDisposisi?.perihal}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Catatan:</strong>{" "}
                    {selectedDisposisi?.catatan_disposisi}
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={penerimaOptions || []}
                  value={selectedPenerimaLanjut}
                  onChange={(event, newValue) =>
                    setSelectedPenerimaLanjut(newValue)
                  }
                  loading={loadingPenerima}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      label="Pilih Penerima Disposisi"
                      variant="standard"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingPenerima ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.value}
                        label={option.label}
                        avatar={
                          option.avatar ? (
                            <img
                              src={option.avatar}
                              alt={option.label}
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                              }}
                            />
                          ) : null
                        }
                      />
                    ))
                  }
                  renderOption={(props, option) => (
                    <li {...props}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {option.avatar && (
                          <img
                            src={option.avatar}
                            alt={option.label}
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                            }}
                          />
                        )}
                        <span>{option.label}</span>
                      </div>
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  label="Tingkat Perintah"
                  value={tingkatPerintah}
                  onChange={(e) => setTingkatPerintah(e.target.value)}
                  fullWidth
                  variant="standard"
                  required
                >
                  <MenuItem value="segera">Segera</MenuItem>
                  <MenuItem value="biasa">Biasa</MenuItem>
                  <MenuItem value="penting">Penting</MenuItem>
                  <MenuItem value="sangat_penting">Sangat Penting</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Catatan Disposisi"
                  multiline
                  rows={4}
                  fullWidth
                  value={catatanDisposisi}
                  onChange={(e) => setCatatanDisposisi(e.target.value)}
                  variant="standard"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <MDTypography variant="h6">Upload File Disposisi</MDTypography>
                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontWeight="regular"
                  >
                    * Maksimal total ukuran file: 1MB
                    <br />* Format yang didukung: PDF, DOC, DOCX, XLS, XLSX,
                    JPG, JPEG
                  </MDTypography>
                </MDBox>
                {useMemo(
                  () => (
                    <MDDropzone
                      options={dropzoneOptionsLanjutkan}
                      onChange={handleLanjutkanDropzoneChange}
                      ref={lanjutkanDropzoneRef}
                    />
                  ),
                  [dropzoneOptionsLanjutkan]
                )}
                {showSizeWarning && (
                  <MDBox mt={2}>
                    <MDTypography
                      variant="caption"
                      color="error"
                      fontWeight="regular"
                    >
                      ⚠️ Total ukuran file ({totalSize.toFixed(2)} MB) melebihi
                      batas maksimal (1 MB)
                    </MDTypography>
                  </MDBox>
                )}
                <MDBox mt={1}>
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontWeight="regular"
                  >
                    Total ukuran: {totalSize.toFixed(2)} MB / 1 MB
                  </MDTypography>
                </MDBox>
                {files.length > 0 && (
                  <MDBox mt={2}>
                    <MDTypography variant="subtitle2" fontWeight="medium">
                      File yang akan diunggah:
                    </MDTypography>
                    <List>
                      {files.map((file, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={file.name}
                            secondary={`${(file.size / (1024 * 1024)).toFixed(
                              2
                            )} MB`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </MDBox>
                )}
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLanjutkanDialog}>Batal</Button>
          <Button
            onClick={handleSubmitLanjutkan}
            variant="contained"
            color="info"
            disabled={
              showSizeWarning ||
              selectedPenerimaLanjut.length === 0 ||
              !catatanDisposisi ||
              !tingkatPerintah
            }
          >
            Lanjutkan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Pilihan Tindak Lanjut */}
      <Dialog
        open={openPilihanDialog}
        onClose={handleClosePilihanDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center">
            <SendIcon sx={{ mr: 1 }} />
            Pilih Tindak Lanjut
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={isMobile ? 2 : 3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDTypography variant="h6" mb={2}>
                  Detail Disposisi
                </MDTypography>
                <MDBox mt={2}>
                  <MDTypography variant="body2">
                    <strong>No. Surat:</strong> {selectedDisposisi?.nomor_surat}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Perihal:</strong> {selectedDisposisi?.perihal}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Catatan:</strong>{" "}
                    {selectedDisposisi?.catatan_disposisi}
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <MDTypography variant="h6" mb={2}>
                  Pilih Tindak Lanjut
                </MDTypography>
                <MDBox display="flex" gap={2}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={() => handlePilihTindakLanjut("selesai")}
                    startIcon={<CheckCircleIcon />}
                  >
                    Selesaikan Disposisi
                  </Button>
                  <Button
                    variant="contained"
                    color="info"
                    fullWidth
                    onClick={() => handlePilihTindakLanjut("lanjutkan")}
                    startIcon={<SendIcon />}
                  >
                    Lanjutkan ke Penerima Lain
                  </Button>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePilihanDialog}>Batal</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog untuk menampilkan daftar penerima lanjutan */}
      <Dialog
        open={openLanjutanDialog}
        onClose={handleCloseLanjutanDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <MDTypography variant="h6" fontWeight="bold" color="dark">
            Daftar Penerima Lanjutan
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox p={isMobile ? 1.5 : 2}>
            <Grid container spacing={2}>
              {selectedLanjutan?.disposisi_lanjutan.map((penerima) => {
                const isCompleted =
                  penerima.waktu_selesai &&
                  penerima.waktu_selesai !== null &&
                  penerima.waktu_selesai !== "";
                const isRead =
                  penerima.waktu_baca && penerima.waktu_baca !== null;

                // Parse file_TL_disposisi
                let fileTLArr = [];
                if (Array.isArray(penerima.file_TL_disposisi)) {
                  fileTLArr = penerima.file_TL_disposisi;
                } else if (
                  typeof penerima.file_TL_disposisi === "string" &&
                  penerima.file_TL_disposisi.trim().startsWith("[")
                ) {
                  try {
                    fileTLArr = JSON.parse(penerima.file_TL_disposisi);
                  } catch (e) {
                    fileTLArr = [];
                  }
                }

                return (
                  <Grid item xs={12} key={penerima.penerima_id}>
                    <Card>
                      <CardContent>
                        {/* Penerima Disposisi */}
                        <MDBox mb={1}>
                          <MDBox
                            display="flex"
                            alignItems="center"
                            gap={1.5}
                            mb={1}
                          >
                            <MDAvatar
                              src={
                                penerima.profil
                                  ? `${API_ESA_OFFICE_UPLOAD}${penerima.profil}`
                                  : null
                              }
                              alt={penerima.nama}
                              size="sm"
                            />
                            <MDBox>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="medium"
                              >
                                Penerima:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {penerima.nama}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        </MDBox>

                        {/* Status */}
                        <MDBox mb={1}>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="medium"
                            mb={0.5}
                          >
                            Status:
                          </MDTypography>
                          <MDBox display="flex" gap={1} flexWrap="wrap">
                            {isCompleted ? (
                              <Chip
                                icon={<CheckCircleIcon />}
                                label="Selesai"
                                color="success"
                                size="small"
                              />
                            ) : isRead ? (
                              <Chip
                                icon={<ScheduleIcon />}
                                label="Dibaca"
                                color="info"
                                size="small"
                              />
                            ) : (
                              <Chip
                                icon={<PendingIcon />}
                                label="Pending"
                                color="warning"
                                size="small"
                              />
                            )}
                          </MDBox>
                        </MDBox>

                        {/* Waktu Dibaca */}
                        {isRead && penerima.waktu_baca && (
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Waktu Dibaca:
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {formatDate(penerima.waktu_baca)}
                            </MDTypography>
                          </MDBox>
                        )}

                        {/* Waktu Selesai */}
                        {isCompleted && penerima.waktu_selesai && (
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Waktu Selesai:
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {formatDate(penerima.waktu_selesai)}
                            </MDTypography>
                          </MDBox>
                        )}

                        {/* Keterangan TL */}
                        {penerima.keterangan_tl && (
                          <MDBox mb={1}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                              display="block"
                              mb={0.5}
                            >
                              Keterangan:
                            </MDTypography>
                            <MDTypography
                              variant="body2"
                              color="text"
                              component="span"
                              sx={{ whiteSpace: "pre-wrap" }}
                            >
                              {renderTextWithLinks(penerima.keterangan_tl)}
                            </MDTypography>
                          </MDBox>
                        )}

                        {/* File Tindak Lanjut */}
                        <MDBox mb={1}>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="medium"
                            mb={0.5}
                          >
                            File Tindak Lanjut ({fileTLArr.length}):
                          </MDTypography>
                          {fileTLArr.length > 0 ? (
                            <MDBox
                              display="flex"
                              flexDirection="column"
                              gap={1}
                            >
                              {fileTLArr.map((file, idx) => {
                                const filename = file.split("/").pop();
                                return (
                                  <Button
                                    key={idx}
                                    href={`${API_ESA_OFFICE_UPLOAD}${file}`}
                                    target="_blank"
                                    variant="contained"
                                    size="small"
                                    startIcon={getFileIcon(filename)}
                                    sx={{ justifyContent: "flex-start" }}
                                  >
                                    {filename}
                                  </Button>
                                );
                              })}
                            </MDBox>
                          ) : (
                            <MDTypography
                              variant="body2"
                              color="text.secondary"
                            >
                              Belum ada file tindak lanjut
                            </MDTypography>
                          )}
                        </MDBox>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseLanjutanDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog untuk menampilkan PDF tindak lanjut */}
      <Dialog
        open={openTindakLanjutPdfDialog}
        onClose={handleCloseTindakLanjutPdfDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <MDBox display="flex" alignItems="center">
              <PictureAsPdfIcon sx={{ mr: 1, color: "error.main" }} />
              <MDTypography variant="h6">File Tindak Lanjut</MDTypography>
            </MDBox>
            <IconButton onClick={handleCloseTindakLanjutPdfDialog} size="small">
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {selectedTindakLanjutPdf ? (
              <iframe
                src={selectedTindakLanjutPdf}
                width="100%"
                height="600px"
                title="PDF Viewer"
                style={{ border: "none" }}
                onError={(e) => {
                  console.error("Error loading PDF:", e);
                  swal({
                    icon: "error",
                    title: "Oops...",
                    text: "Gagal memuat file PDF",
                    button: "OK",
                  });
                }}
              />
            ) : (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="600px"
              >
                <MDTypography variant="h6" color="text">
                  File tidak tersedia
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseTindakLanjutPdfDialog}
            variant="contained"
            color="error"
            startIcon={<CloseIcon />}
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Chat */}
      <Dialog
        open={openChatDialog}
        onClose={handleCloseChat}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            height: "80vh",
            maxHeight: "600px",
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        {/* Header - Material Dashboard Style */}
        <MDBox
          sx={{
            bgcolor: "info.main",
            color: "white",
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: 2,
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <MDAvatar
              src={selectedChatDisposisi?.profil_pengirim || undefined}
              alt={selectedChatDisposisi?.nama_pengirim}
              size="sm"
              sx={{ border: "2px solid rgba(255,255,255,0.3)" }}
            />
            <MDBox>
              <MDTypography variant="h6" fontWeight="bold" color="white">
                {selectedChatDisposisi?.nama_pengirim || "Pengirim"}
              </MDTypography>
              <MDTypography variant="caption" color="white">
                {selectedChatDisposisi?.perihal || "Disposisi"}
              </MDTypography>
            </MDBox>
          </MDBox>
          <MDBox display="flex" gap={1}>
            <IconButton
              onClick={handleRefreshChat}
              disabled={refreshingChat || loadingChat}
              size="small"
              sx={{
                bgcolor: "white",
                color: refreshingChat ? "grey.500" : "grey.700",
                "&:hover": {
                  bgcolor: "grey.100",
                },
                "&:disabled": {
                  bgcolor: "grey.300",
                  color: "grey.500",
                },
              }}
              title="Refresh Chat"
            >
              {refreshingChat ? (
                <CircularProgress size={20} sx={{ color: "grey.500" }} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
            <IconButton
              onClick={handleCloseChat}
              size="small"
              sx={{
                bgcolor: "white",
                color: "grey.700",
                "&:hover": {
                  bgcolor: "grey.100",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
        </MDBox>
        <DialogContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            p: 0,
          }}
        >
          {loadingChat ? (
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress />
            </MDBox>
          ) : (
            <MDBox
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {chatMessages.length === 0 ? (
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                >
                  <MDTypography variant="body2" color="text.secondary">
                    Belum ada pesan. Mulai percakapan!
                  </MDTypography>
                </MDBox>
              ) : (
                chatMessages.map((message, idx) => {
                  const currentUser =
                    JSON.parse(localStorage.getItem("user")) || {};
                  const currentUserId =
                    userId || currentUser.uid || currentUser.user_id;
                  const isOwnMessage = message.pengirim_id === currentUserId;
                  const isSending = message.isSending;

                  return (
                    <MDBox
                      key={message.id_chat || idx}
                      display="flex"
                      justifyContent={isOwnMessage ? "flex-end" : "flex-start"}
                      sx={{ mb: 0.5, px: 0.5 }}
                    >
                      <MDBox
                        sx={{
                          maxWidth: "75%",
                          minWidth: "100px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: isOwnMessage ? "flex-end" : "flex-start",
                        }}
                      >
                        {!isOwnMessage && (
                          <MDBox
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            mb={0.3}
                            sx={{ px: 0.5 }}
                          >
                            <MDAvatar
                              src={
                                message.pengirim_profil
                                  ? `https://smartdh.rotendaokab.go.id/uploads/${message.pengirim_profil}.jpg`
                                  : undefined
                              }
                              size="xs"
                            />
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                            >
                              {message.pengirim_nama || "-"}
                            </MDTypography>
                          </MDBox>
                        )}

                        <MDBox
                          sx={{
                            p: 1.25,
                            px: 1.5,
                            borderRadius: isOwnMessage
                              ? "7.5px 0 7.5px 7.5px"
                              : "0 7.5px 7.5px 7.5px",
                            backgroundColor: isOwnMessage ? "#4caf50" : "white",
                            color: isOwnMessage ? "white" : "text.primary",
                            wordBreak: "break-word",
                            boxShadow: 1,
                            position: "relative",
                            opacity: isSending ? 0.7 : 1,
                            border: isOwnMessage ? "none" : "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <MDTypography
                            variant="body2"
                            sx={{
                              color: isOwnMessage ? "white" : "text.primary",
                              fontSize: "0.95rem",
                              lineHeight: 1.4,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {message.pesan}
                            {isSending && (
                              <MDBox
                                component="span"
                                sx={{
                                  display: "inline-block",
                                  ml: 0.5,
                                  verticalAlign: "middle",
                                }}
                              >
                                <CircularProgress
                                  size={12}
                                  sx={{ color: "white" }}
                                />
                              </MDBox>
                            )}
                          </MDTypography>
                        </MDBox>

                        <MDBox
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                          sx={{ mt: 0.2, px: 0.5 }}
                        >
                          <MDTypography
                            variant="caption"
                            sx={{
                              fontSize: "0.65rem",
                              color: "#4caf50",
                              fontWeight: 400,
                            }}
                          >
                            {formatChatTime(message.waktu_kirim)}{" "}
                          </MDTypography>
                          {isOwnMessage && !isSending && (
                            <MDBox
                              sx={{
                                width: "12px",
                                height: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CheckCircleIcon
                                sx={{
                                  fontSize: "12px",
                                  color: message.waktu_baca
                                    ? "rgba(11, 183, 25, 0.7)"
                                    : "rgba(255,255,255,0.6)",
                                }}
                              />
                            </MDBox>
                          )}
                          {isOwnMessage && message.waktu_baca && (
                            <MDTypography
                              variant="caption"
                              sx={{
                                fontSize: "0.6rem",
                                color: "rgba(11, 183, 25, 0.7)",
                                fontStyle: "italic",
                              }}
                            >
                              Dibaca {formatChatTime(message.waktu_baca)}
                            </MDTypography>
                          )}
                          {isOwnMessage && !isSending && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeleteMessage(message.id_chat)
                              }
                              sx={{
                                width: "20px",
                                height: "20px",
                                color: "rgba(251, 27, 15, 0.7)",
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: "14px" }} />
                            </IconButton>
                          )}
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </MDBox>
          )}
        </DialogContent>
        <MDBox
          sx={{
            bgcolor: "grey.50",
            p: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          <TextField
            fullWidth
            placeholder="Ketik pesan..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            multiline
            maxRows={4}
            size="small"
            disabled={loadingChat}
            sx={{
              bgcolor: "white",
              borderRadius: "20px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                "& fieldset": {
                  borderColor: "rgba(0,0,0,0.1)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(0,0,0,0.2)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#000000",
                },
              },
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || loadingChat}
            sx={{
              bgcolor: "#000000",
              color: "white",
              width: "40px",
              height: "40px",
              "&:hover": {
                bgcolor: "#333333",
              },
              "&:disabled": {
                bgcolor: "#cccccc",
              },
            }}
          >
            <SendIcon />
          </IconButton>
        </MDBox>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default DisposisiTindakLanjut;
